import React, { useState, ReactElement, isValidElement } from 'react';

import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

import processInput from '../../utils/formInput';

import { formClasses } from './utils/FormStyles';
import { Label } from './utils/Label';

interface SelectFieldProps {
  id: string;
  label?: string;
  className?: string;
  placeholder?: string;
  value: string;
  onChange?: (value: string) => void;
  error?: boolean;
  tooltip?: string;
  shouldShowTooltip?: boolean;
  disabled?: boolean;
  errorMessage?: string;
  link?: string;
  name: string;
  children?: React.ReactNode;
  onBlur?: (e: React.FocusEvent<HTMLSelectElement>) => void;
  updateFn?: (fieldName: string, value: unknown) => void;
  validateFn?: (fieldName: string) => void;
}

export function SelectField({
  id,
  label = '',
  className = '',
  placeholder = 'Select an option',
  value,
  onChange,
  error = false,
  tooltip = '',
  shouldShowTooltip = false,
  disabled = false,
  errorMessage = '',
  link = '',
  name,
  children,
  updateFn,
  validateFn,
  ...props
}: SelectFieldProps) {
  const [selectedColorClass, setSelectedColorClass] = useState('');
  const [showTooltip, setShowTooltip] = useState(false);

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    const finalValue = selectedValue === '' ? '' : selectedValue;

    if (updateFn && validateFn) {
      processInput(updateFn, validateFn, name, finalValue);
    } else if (onChange) {
      onChange(finalValue);
    }

    setSelectedColorClass(selectedValue ? 'text-gray-900' : 'text-gray-400');
  };

  const handleMouseEnter = () => setShowTooltip(true);
  const handleMouseLeave = () => setShowTooltip(false);

  const optionElements = React.Children.toArray(children).filter(
    (child) => isValidElement(child) && child.type === 'option',
  ) as ReactElement[];

  const shouldRenderAsButtons =
    optionElements.length > 0 && optionElements.length <= 5;

  const handleButtonClick = (value: string) => {
    if (updateFn && validateFn) {
      processInput(updateFn, validateFn, name, value);
    } else if (onChange) {
      onChange(value);
    }

    if (props.onBlur) {
      props.onBlur({
        target: { id, name, value },
        type: 'blur',
      } as React.FocusEvent<HTMLSelectElement>);
    }
  };

  const buttonGroup = (
    <div
      className={clsx(
        'inline-flex w-full flex-wrap overflow-hidden rounded-md border border-gray-300 ring-offset-2 ring-offset-blue-100 hover:ring-1',
        className,
      )}
    >
      {optionElements.map((child, index) => {
        const isSelected = child.props.value === value;
        const isFirst = index === 0;
        const isLast = index === optionElements.length - 1;

        return (
          <button
            key={child.props.value}
            disabled={disabled}
            type="button"
            className={clsx(
              '-ml-px flex-1 p-4 text-sm font-medium transition-all focus:z-10',
              isSelected
                ? 'bg-turquoise text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100',
              {
                'rounded-l-md': isFirst,
                'rounded-r-md border-t sm:border-t-0':
                  isLast && optionElements.length === 5,
                'cursor-not-allowed opacity-50': disabled,
                'border-l': !isFirst,
              },
            )}
            onClick={() => handleButtonClick(child.props.value)}
          >
            {child.props.children}
          </button>
        );
      })}
    </div>
  );

  const normalSelectField = (
    <div className="size-full relative">
      <select
        aria-invalid={error ? 'true' : undefined}
        disabled={disabled}
        id={id}
        name={name}
        value={value !== undefined ? value : ''}
        className={clsx(
          formClasses,
          { 'border-red-500': error },
          { 'cursor-not-allowed': disabled },
          `${selectedColorClass}`,
        )}
        onBlur={props.onBlur}
        onChange={handleSelectChange}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <option disabled hidden className="text-gray-400" value="">
          {placeholder}
        </option>
        {children}
      </select>
      <div
        className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 ${selectedColorClass}`}
      >
        <ChevronDownIcon className="h-4 w-4" />
      </div>
    </div>
  );

  return (
    <div className={className}>
      {label && (
        <Label id={id} link={link}>
          {label}
        </Label>
      )}

      <div className="relative">
        {shouldRenderAsButtons ? buttonGroup : normalSelectField}

        {showTooltip && shouldShowTooltip && (
          <div className="absolute left-0 mt-2 rounded-md border border-gray-200 bg-white p-2 text-gray-600 shadow-md">
            {tooltip}
          </div>
        )}
        {error && !!errorMessage ? (
          <div className="mt-1 text-red-500">{errorMessage}</div>
        ) : null}
      </div>
    </div>
  );
}
